import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Fab,
  Zoom,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  LinearProgress,
  Chip,
  FormLabel,
  RadioGroup,
  Radio,
  CardContent,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Visibility,
  Assessment,
  Close,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Psychology,
  Compare,
  SmartToy,
  AutoAwesome,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchAnalyses,
  createEnhancedAnalysis,
  clearError,
} from '../store/slices/analysesSlice';

import { LoadingSpinner, SearchBar, StatusChip, ImageUploadBox } from '../components/common';
import { AnalysisStatsCards, EmptyAnalysisState } from '../components/analysis';

import { Product, LLMProvider } from '../types/api';

const LLMProviderCard = styled(Paper)(({ theme, selected }: { theme?: any; selected: boolean }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: selected ? `2px solid ${theme.palette.primary.main}` : `2px solid transparent`,
  backgroundColor: selected ? theme.palette.primary.light + '10' : 'transparent',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const AnalysesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  // Redux state
  const {
    analyses,
    llmProviders,
    processingAnalysis,
    loading,
    error
  } = useAppSelector((state) => state.analyses);

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [selectedTestImages, setSelectedTestImages] = useState<File[]>([]);
  const [testImagePreviewUrls, setTestImagePreviewUrls] = useState<string[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [newAnalysis, setNewAnalysis] = useState({
    test_product_name: '',
    test_product_seller: '',
    test_product_url: '',
    test_product_price: '',
    reference_product_id: '',
    use_llm: true,
    llm_provider: 'openai',
  });
  const [referenceProducts, setReferenceProducts] = useState<Product[]>([]);

  // Default LLM providers configuration
  const defaultLLMProviders: LLMProvider[] = [
    {
      name: 'openai',
      display_name: 'OpenAI GPT',
      model: 'gpt-4o-mini',
      description: 'Advanced reasoning and analysis with GPT-4o-mini',
      available: true,
    },
    {
      name: 'groq',
      display_name: 'Groq Llama',
      model: 'llama-3.1-8b-instant',
      description: 'Ultra-fast inference with Llama-3.1-8b-instant',
      available: true,
    },
  ];

  useEffect(() => {
    dispatch(fetchAnalyses({}));
    loadReferenceProducts();
  }, [dispatch]);

  const loadReferenceProducts = async () => {
    try {
      const response = await fetch('https://server.bitsindia.in/api/products/', {
        headers: {
          'Authorization': 'Bearer demo-token',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReferenceProducts(data.products || []);
      } else {
        console.error('Failed to load reference products');
      }
    } catch (error) {
      console.error('Error loading reference products:', error);
    }
  };

  const handleTestImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setSelectedTestImages(files);
      const urls = files.map(file => URL.createObjectURL(file));
      setTestImagePreviewUrls(urls);
    }
  };

  const removeTestImage = (index: number) => {
    const newImages = selectedTestImages.filter((_, i) => i !== index);
    const newUrls = testImagePreviewUrls.filter((_, i) => i !== index);
    setSelectedTestImages(newImages);
    setTestImagePreviewUrls(newUrls);
  };

  const handleRunAnalysis = async () => {
    try {
      const analysisData = {
        test_product_name: newAnalysis.test_product_name,
        test_product_seller: newAnalysis.test_product_seller,
        test_product_url: newAnalysis.test_product_url,
        test_product_price: newAnalysis.test_product_price ? parseFloat(newAnalysis.test_product_price) : undefined,
        reference_product_id: newAnalysis.reference_product_id ? parseInt(newAnalysis.reference_product_id) : undefined,
        use_llm: newAnalysis.use_llm,
        llm_provider: newAnalysis.llm_provider,
        test_images: selectedTestImages,
        use_enhanced_analysis: true,
        use_analytics: true,
        product_data: {
          name: newAnalysis.test_product_name,
          brand: 'Unknown',
          category: 'General',
          description: `Test product: ${newAnalysis.test_product_name}`
        },
      };

      // Dispatch the createEnhancedAnalysis action
      await dispatch(createEnhancedAnalysis(analysisData)).unwrap();

      // Reset form on success
      setNewAnalysis({
        test_product_name: '',
        test_product_seller: '',
        test_product_url: '',
        test_product_price: '',
        reference_product_id: '',
        use_llm: true,
        llm_provider: 'openai',
      });
      setSelectedTestImages([]);
      setTestImagePreviewUrls([]);
      setAddDialogOpen(false);
      setActiveStep(0);
      setAnalysisProgress(0);

    } catch (error) {
      console.error('Analysis failed:', error);
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const filteredAnalyses = Array.isArray(analyses) ? analyses.filter(analysis =>
    analysis.test_product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (analysis.test_product_seller && analysis.test_product_seller.toLowerCase().includes(searchTerm.toLowerCase()))
  ) : [];

  const getResultIcon = (isCounterfeit: boolean, confidence: number) => {
    if (isCounterfeit) {
      return confidence > 0.8 ? <ErrorIcon color="error" /> : <Warning color="warning" />;
    }
    return <CheckCircle color="success" />;
  };

  const canProceedToNext = () => {
    switch (activeStep) {
      case 0:
        return newAnalysis.test_product_name.trim() !== '';
      case 1:
        return selectedTestImages.length > 0;
      case 2:
        return newAnalysis.reference_product_id !== '';
      case 3:
        return true;
      default:
        return false;
    }
  };

  const getSelectedLLMProvider = () => {
    return llmProviders.find(p => p.name === newAnalysis.llm_provider) || defaultLLMProviders[0];
  };

  if (loading) {
    return <LoadingSpinner message="Loading analyses..." />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h3" component="h1" fontWeight="bold" color="primary">
              Product Analysis
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Detect counterfeit products using AI-powered analysis
            </Typography>
          </Box>
          <Zoom in={true}>
            <Fab
              color="primary"
              onClick={() => setAddDialogOpen(true)}
              sx={{ boxShadow: 4 }}
            >
              <Add />
            </Fab>
          </Zoom>
        </Box>

        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search analyses by product name or seller..."
        />
      </Box>

      {error && (
        <Alert
          severity={error.includes('successfully') ? 'success' : 'error'}
          sx={{ mb: 3 }}
          onClose={() => dispatch(clearError())}
        >
          {error}
        </Alert>
      )}

      <AnalysisStatsCards analyses={analyses} />

      {filteredAnalyses.length === 0 ? (
        <EmptyAnalysisState
          searchTerm={searchTerm}
          onAddAnalysis={() => setAddDialogOpen(true)}
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Analysis</TableCell>
                <TableCell>Test Product</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Risk Level</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAnalyses.map((analysis) => (
                <TableRow key={analysis.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <Assessment />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {analysis.analysis_id.slice(0, 8)}...
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {analysis.processing_time ? `${analysis.processing_time.toFixed(1)}s` : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      {analysis.test_product_name}
                    </Typography>
                    {analysis.test_product_seller && (
                      <Typography variant="body2" color="text.secondary">
                        Seller: {analysis.test_product_seller}
                      </Typography>
                    )}
                    {analysis.test_product_price && (
                      <Typography variant="body2" color="text.secondary">
                        Price: ${analysis.test_product_price}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getResultIcon(analysis.is_counterfeit, analysis.confidence_score)}
                      <Chip
                        label={analysis.is_counterfeit ? 'Counterfeit' : 'Authentic'}
                        color={analysis.is_counterfeit ? 'error' : 'success'}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {(analysis.confidence_score * 100).toFixed(1)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={analysis.confidence_score * 100}
                        sx={{ width: 60, height: 4, borderRadius: 2 }}
                        color={analysis.confidence_score > 0.8 ? 'error' : analysis.confidence_score > 0.5 ? 'warning' : 'success'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={analysis.risk_level} variant="risk" />
                  </TableCell>
                  <TableCell>
                    <StatusChip status={analysis.status} variant="analysis" />
                  </TableCell>
                  <TableCell>
                    {new Date(analysis.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small"
                      onClick={() => navigate(`/analyses/${analysis.analysis_id}`)}
                      title="View Analysis Details"
                    >
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <Assessment />
            </Avatar>
            <Box>
              <Typography variant="h5">New Product Analysis</Typography>
              <Typography variant="body2" color="text.secondary">
                Analyze a product for authenticity using AI detection
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 2 }}>
            <Step>
              <StepLabel>Product Information</StepLabel>
              <StepContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Test Product Name"
                      value={newAnalysis.test_product_name}
                      onChange={(e) => setNewAnalysis({ ...newAnalysis, test_product_name: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Seller/Store"
                      value={newAnalysis.test_product_seller}
                      onChange={(e) => setNewAnalysis({ ...newAnalysis, test_product_seller: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Price ($)"
                      type="number"
                      value={newAnalysis.test_product_price}
                      onChange={(e) => setNewAnalysis({ ...newAnalysis, test_product_price: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Product URL (optional)"
                      value={newAnalysis.test_product_url}
                      onChange={(e) => setNewAnalysis({ ...newAnalysis, test_product_url: e.target.value })}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!canProceedToNext()}
                  >
                    Next
                  </Button>
                </Box>
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Upload Test Images</StepLabel>
              <StepContent>
                <input
                  id="test-image-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleTestImageUpload}
                  style={{ display: 'none' }}
                />
                <ImageUploadBox
                  onClick={() => document.getElementById('test-image-upload')?.click()}
                  title="Upload Test Images"
                  subtitle="Upload images of the product you want to analyze"
                  sx={{ my: 2 }}
                />

                {testImagePreviewUrls.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Images ({testImagePreviewUrls.length})
                    </Typography>
                    <Grid container spacing={2}>
                      {testImagePreviewUrls.map((url, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Box sx={{ position: 'relative' }}>
                            <img
                              src={url}
                              alt={`Preview ${index + 1}`}
                              style={{
                                width: '100%',
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 8,
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => removeTestImage(index)}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
                              }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                  <Button onClick={handleBack}>Back</Button>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!canProceedToNext()}
                  >
                    Next
                  </Button>
                </Box>
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Select Reference Product</StepLabel>
              <StepContent>
                <FormControl fullWidth>
                  <InputLabel>Select Reference Product</InputLabel>
                  <Select
                    value={newAnalysis.reference_product_id}
                    onChange={(e) => setNewAnalysis({ ...newAnalysis, reference_product_id: e.target.value })}
                    label="Select Reference Product"
                  >
                    {referenceProducts.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        <div>
                          <Typography variant="subtitle1">{product.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {product.brand_name} • SKU: {product.sku || 'N/A'}
                          </Typography>
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                  <Button onClick={handleBack}>Back</Button>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!canProceedToNext()}
                  >
                    Next
                  </Button>
                </Box>
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Analysis Settings</StepLabel>
              <StepContent>
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newAnalysis.use_llm}
                        onChange={(e) => setNewAnalysis({ ...newAnalysis, use_llm: e.target.checked })}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Psychology />
                        <Box>
                          <Typography variant="body2">Enable LLM Analysis</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Use AI language model for enhanced analysis
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />

                  {newAnalysis.use_llm && (
                    <Box sx={{ mt: 3 }}>
                      <FormLabel component="legend" sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          Choose AI Model
                        </Typography>
                      </FormLabel>
                      
                      <RadioGroup
                        value={newAnalysis.llm_provider}
                        onChange={(e) => setNewAnalysis({ ...newAnalysis, llm_provider: e.target.value })}
                      >
                        <Grid container spacing={2}>
                          {(llmProviders.length > 0 ? llmProviders : defaultLLMProviders).map((provider) => (
                            <Grid item xs={12} md={6} key={provider.name}>
                              <FormControlLabel
                                value={provider.name}
                                control={<Radio />}
                                label=""
                                sx={{ margin: 0, width: '100%' }}
                              />
                              <LLMProviderCard
                                selected={newAnalysis.llm_provider === provider.name}
                                onClick={() => setNewAnalysis({ ...newAnalysis, llm_provider: provider.name })}
                                sx={{ mt: -1 }}
                              >
                                <CardContent sx={{ p: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                                      {provider.name === 'openai' ? <AutoAwesome /> : <SmartToy />}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="subtitle1" fontWeight="medium">
                                        {provider.display_name}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {provider.model}
                                      </Typography>
                                    </Box>
                                    {newAnalysis.llm_provider === provider.name && (
                                      <CheckCircle color="primary" />
                                    )}
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {provider.description}
                                  </Typography>
                                </CardContent>
                              </LLMProviderCard>
                            </Grid>
                          ))}
                        </Grid>
                      </RadioGroup>
                    </Box>
                  )}
                </Box>

                {processingAnalysis && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" gutterBottom>
                      Running analysis with {getSelectedLLMProvider()?.display_name}... {analysisProgress}%
                    </Typography>
                    <LinearProgress variant="determinate" value={analysisProgress} />
                  </Box>
                )}

                <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                  <Button onClick={handleBack} disabled={processingAnalysis}>
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleRunAnalysis}
                    disabled={processingAnalysis}
                    startIcon={processingAnalysis ? <CircularProgress size={20} /> : <Compare />}
                  >
                    {processingAnalysis ? 'Analyzing...' : `Run Analysis`}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => {
              setAddDialogOpen(false);
              setActiveStep(0);
              setSelectedTestImages([]);
              setTestImagePreviewUrls([]);
            }}
            disabled={processingAnalysis}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AnalysesPage;